// Admin → Ops → eBay messages (2026-10-29,
// docs/decisions/2026-10-29-ebay-new-messages-attach-listing.md): per eBay
// account per Eastern day, the member messages CXHub sent and what eBay said.
// It measures whether attaching the listing keeps new-conversation messages
// out of eBay's daily limit (50044): the owner reads it daily for a week after
// deploy. Counts only, from server/src/repositories/ebay-message-sends.js;
// no message text, no buyer.

import { useEffect, useState } from 'react'
import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, CircularProgress, Alert, Button, Stack } from '@mui/material'
import { fetchEbayMessageCounts } from './api.js'
import { accountLabel } from './ebayDailyLimit.js'

const COLUMNS = [
  ['newConversationSends', 'New-conversation sends', 'Message API sends that start a conversation with a buyer'],
  ['withListing', 'With listing', 'Sent with the listing attached'],
  ['listingRefused', 'Listing refused', 'eBay refused the listing reference; resent without it'],
  ['newConversationAccepted', 'Accepted', 'New-conversation messages eBay took (Message API or Trading API)'],
  ['refused50044', '50044', "Message API refusals for eBay's daily limit"],
  ['refused20957', '20957', "Trading API refusals for eBay's daily limit"],
  ['tradingFallbacks', 'Trading API fallbacks', 'AddMemberMessageAAQToPartner sends after a Message API refusal'],
  ['sentInSellerHub', 'Sent in Seller Hub', 'Recorded by a person (Sent response through eBay Portal) after the limit'],
  ['replies', 'Replies', 'Replies inside an existing buyer conversation'],
]

const SOURCES = [['reply', 'reply'], ['step_person', 'step (person)'], ['step_automation', 'automation'], ['other_actions', 'Other Actions']]

export function limitSourceText(bySource) {
  return SOURCES.filter(([k]) => (bySource?.[k] ?? 0) > 0).map(([k, label]) => `${label} ${bySource[k]}`).join(', ')
}

export default function EbayMessagesView() {
  const [rows, setRows] = useState(null)
  const [error, setError] = useState(null)
  const [days, setDays] = useState(14)
  const load = () => {
    setError(null)
    fetchEbayMessageCounts(days).then((r) => setRows(r?.days ?? [])).catch((e) => setError(e?.message ?? 'Could not load the counts'))
  }
  useEffect(load, [days])
  return (
    <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }} data-testid="ebay-messages-view">
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6">eBay messages</Typography>
        <Button size="small" onClick={() => setDays(days === 14 ? 30 : 14)}>{days === 14 ? 'Last 30 days' : 'Last 14 days'}</Button>
        <Button size="small" onClick={load}>Refresh</Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Per eBay account per Eastern day: new-conversation messages and whether they carried the listing, eBay&apos;s daily-limit
        refusals (50044 on the Message API, 20957 on the Trading API), the Trading API fallbacks, the messages a person sent in
        Seller Hub, and replies. A day&apos;s first refusal shows how many sends came before it.
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {!rows && !error && <CircularProgress size={20} />}
      {rows && rows.length === 0 && <Typography variant="body2">No eBay messages recorded in this window.</Typography>}
      {rows && rows.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Day (ET)</TableCell>
              <TableCell>Account</TableCell>
              {COLUMNS.map(([key, label, title]) => <TableCell key={key} align="right" title={title}>{label}</TableCell>)}
              <TableCell>Limit hit by</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={`${r.day}-${r.account}`}>
                <TableCell>{r.day}</TableCell>
                <TableCell>{accountLabel(r.account)}</TableCell>
                {COLUMNS.map(([key]) => <TableCell key={key} align="right">{r[key] ?? 0}</TableCell>)}
                <TableCell>{limitSourceText(r.limitBySource)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  )
}
